import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import {
	ArrowDownTrayIcon,
	EllipsisVerticalIcon,
	EyeIcon,
	ShareIcon,
} from "@heroicons/react/24/outline";
import { format } from "date-fns";
import { type CSSProperties, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useChatActions } from "../../contexts/chat-actions-context";
import { useAnalytics } from "../../lib/analytics";
import { formatFileSize, getFileExtension } from "../../lib/utils/file";
import { getDocumentViewUrl } from "../../services/documentService";
import type { Document } from "../../types/document";
import styles from "./FileCard.module.css";

// Share (email/SMS/WhatsApp) isn't operational yet. Keep the menu item visible
// but greyed + non-functional; clicks are logged so we can gauge demand. Flip
// to true to re-enable the real share flow.
const SHARE_ENABLED = false;

interface FileCardProps {
	document: Document;
	t: (key: string, options?: Record<string, string>) => string;
	onShare: (document: Document) => void;
}

export function FileCard({ document: doc, t, onShare }: FileCardProps) {
	const { triggerMessage } = useChatActions();
	const { logEvent } = useAnalytics();
	const navigate = useNavigate();

	const [menuOpen, setMenuOpen] = useState(false);
	// Menu is rendered in a portal (fixed-positioned) so the horizontally
	// scrolling carousel's overflow can't clip it. Coords are computed from the
	// trigger button on open.
	const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });
	const buttonRef = useRef<HTMLButtonElement>(null);
	const menuRef = useRef<HTMLDivElement>(null);

	function openMenu() {
		const rect = buttonRef.current?.getBoundingClientRect();
		if (rect) {
			// CSS anchors the menu with `inset-inline-end: var(--menu-right)`, which
			// resolves to the physical right edge in LTR and the physical left edge
			// in RTL — so the offset must be measured from the matching edge.
			const isRtl =
				globalThis.document.documentElement.getAttribute("dir") === "rtl";
			setMenuPos({
				top: rect.bottom + 6,
				right: isRtl ? rect.left : globalThis.window.innerWidth - rect.right,
			});
		}
		setMenuOpen(true);
	}

	useEffect(() => {
		if (!menuOpen) return;
		function handlePointerDown(event: MouseEvent) {
			const target = event.target as Node;
			if (
				buttonRef.current?.contains(target) ||
				menuRef.current?.contains(target)
			) {
				return;
			}
			setMenuOpen(false);
		}
		// Any scroll/resize invalidates the anchored position — just close.
		function close() {
			setMenuOpen(false);
		}
		document.addEventListener("mousedown", handlePointerDown);
		globalThis.window.addEventListener("scroll", close, true);
		globalThis.window.addEventListener("resize", close);
		return () => {
			document.removeEventListener("mousedown", handlePointerDown);
			globalThis.window.removeEventListener("scroll", close, true);
			globalThis.window.removeEventListener("resize", close);
		};
	}, [menuOpen]);

	const fileExtension = getFileExtension(doc.mimeType, doc.name);
	const fileSize = formatFileSize(doc.fileSize);
	const uploadDate = format(new Date(doc.uploadedAt), "MMM d, yyyy");

	async function handleView() {
		logEvent("file_view_pressed", undefined, { documentId: doc.id });
		try {
			const { url } = await getDocumentViewUrl(doc.id);
			if (Capacitor.isNativePlatform()) {
				await Browser.open({ url });
			} else {
				globalThis.window.open(url, "_blank");
			}
		} catch {
			toast.error(t("actions.viewError"));
		}
	}

	function handleShare() {
		// Share is disabled (not operational yet) but kept visible + clickable so
		// we capture demand. Log the click; do NOT open the share modal.
		if (!SHARE_ENABLED) {
			logEvent("file_share_disabled_clicked", undefined, {
				documentId: doc.id,
			});
			return;
		}
		logEvent("file_share_pressed", undefined, { documentId: doc.id });
		onShare(doc);
	}

	async function handleDownload() {
		logEvent("file_download_pressed", undefined, { documentId: doc.id });
		try {
			const { url } = await getDocumentViewUrl(doc.id, true);
			if (Capacitor.isNativePlatform()) {
				await Browser.open({ url });
			} else {
				globalThis.window.open(url, "_blank");
			}
			toast.success(t("actions.downloadStarted"));
		} catch {
			toast.error(t("actions.viewError"));
		}
	}

	function handleAskEve() {
		logEvent("file_ask_eve_pressed", undefined, { documentId: doc.id });
		// Send a STABLE internal reference — NOT a presigned URL, whose X-Amz-*
		// signature would land in the chat request body/logs. era-api's
		// inlineOurDocumentParts resolves `/api/documents/:id/raw` to inline bytes
		// server-side (ownership enforced by the authed user), so this works in
		// every env without the browser fetching the file.
		triggerMessage(
			t("actions.askEveQuestion", { filename: doc.name }),
			undefined,
			[{ url: `/api/documents/${doc.id}/raw`, mediaType: doc.mimeType }],
		);
		// Start a fresh chat for the file analysis (?new=true) rather than
		// appending to the most-recently-resumed conversation.
		navigate("/chat?new=true");
	}

	// Run a menu action, then close the menu.
	function runFromMenu(action: () => void) {
		setMenuOpen(false);
		action();
	}

	return (
		<div className={styles.card}>
			<div className={styles.infoArea}>
				<div className={styles.nameRow}>
					<span className={styles.fileName}>{doc.name}</span>
					<span className={styles.typeBadge}>{fileExtension}</span>
				</div>
				<span className={styles.details}>
					{fileSize} &middot; {uploadDate}
				</span>
			</div>
			<div className={styles.actionsRow}>
				<button
					type="button"
					className={styles.askEveButton}
					onClick={handleAskEve}
				>
					<img
						src="/images/ask-eve-icon.png"
						alt=""
						className={styles.eveIcon}
					/>
					<span className={styles.askEveLabel}>{t("actions.askEve")}</span>
				</button>
				<button
					ref={buttonRef}
					type="button"
					className={styles.actionButton}
					onClick={() => (menuOpen ? setMenuOpen(false) : openMenu())}
					aria-label={t("actions.moreOptions")}
					aria-haspopup="true"
					aria-expanded={menuOpen}
				>
					<EllipsisVerticalIcon className={styles.actionIcon} />
				</button>
			</div>

			{menuOpen &&
				createPortal(
					<div
						ref={menuRef}
						className={styles.menu}
						role="menu"
						style={
							{
								"--menu-top": `${menuPos.top}px`,
								"--menu-right": `${menuPos.right}px`,
							} as CSSProperties
						}
					>
						<button
							type="button"
							className={styles.menuItem}
							role="menuitem"
							onClick={() => runFromMenu(handleDownload)}
						>
							<ArrowDownTrayIcon className={styles.menuIcon} />
							<span>{t("actions.download")}</span>
						</button>
						<button
							type="button"
							className={`${styles.menuItem}${SHARE_ENABLED ? "" : ` ${styles.menuItemDisabled}`}`}
							role="menuitem"
							aria-disabled={SHARE_ENABLED ? undefined : true}
							onClick={() => runFromMenu(handleShare)}
						>
							<ShareIcon className={styles.menuIcon} />
							<span>{t("actions.share")}</span>
						</button>
						<button
							type="button"
							className={styles.menuItem}
							role="menuitem"
							onClick={() => runFromMenu(handleView)}
						>
							<EyeIcon className={styles.menuIcon} />
							<span>{t("actions.view")}</span>
						</button>
					</div>,
					document.body,
				)}
		</div>
	);
}
